import { Box, CalendarIcon, CurrencyInput, Radio, RadioGroup } from '@cof/plastic-components';
import { useFormContext, Controller } from 'react-hook-form';
import { InputWrapper } from './SharedStyledComponents';
import { css, styled } from 'styled-components';
import ErrorHandler, { shouldDisplayFormTick, shouldDisplayRadioTick } from './ErrorHandler';
import { DescriptionSection, LabelSection } from '../../../components/TextField/TextField';
import { FORM_SECTIONS } from './FormIDs';
import { useContext, useState } from 'react';
import { FormUtilsContext } from '../QuickCheck';
import Group from '../../../components/Group';
import { withText } from '../../../text/textStore';
import PropTypes from 'prop-types';
import { NarrativeTextHookForm } from '../../../components/NarrativeText/NarrativeText';
import { getAriaDescribedBy } from '../../../utilities/utils';
import { useLandmarkRefsContext } from '../../../components/LandmarkRefsProvider';

const DependentsRadioGroup = styled(RadioGroup)`
    ${({ theme }: any) => css`
        display: flex;
        flex-wrap: wrap;
        gap: ${theme.space.xs};

        /* description text appears on its only line */
        > :nth-child(2) {
            flex-basis: 100%;
        }

        ${theme.breakpoints.mq.mobileMax} {
            /* on mobile the first radio is 100% width, all radios after are split evenly */
            > :nth-child(3) {
                flex-basis: 100%;
            }
            > :nth-child(n + 4) {
                flex: 1;
            }
        }
    `}
`;

const LivingSituationRadioGroup = styled(RadioGroup)`
    ${({ theme }: any) => css`
        display: flex;
        flex-wrap: wrap;
        gap: ${theme.space.xs};
        width: 100%;
        padding-bottom: ${theme.space.xs};

        > legend {
            flex-basis: 100%;
        }

        /* sets all of the radios to be the same size whilst retaining the ability to fill the width */
        > :not(legend) {
            flex: 1 1 calc((100% - 2 * ${theme.space.xs}) / 3);
            max-width: calc((100% - 2 * ${theme.space.xs}) / 3);
        }

        /* mobile is two column with the final radio spanning both */
        ${theme.breakpoints.mq.mobileMax} {
            > :not(legend) {
                flex: 1 1 40%;
                max-width: none;
            }

            > :last-child {
                flex-basis: 100%;
            }
        }
    `}
`;

const cashAdvanceOptions = { Yes: 'Y', No: 'N' };

const FinancialDependencyForm = ({ Text, Text: text, JsxText }: any) => {
    const {
        register,
        formState: { errors, touchedFields },
        getValues,
        watch
    } = useFormContext();
    const residentialStatusOptions = text({ path: 'ResidentialStatus.Options' });
    const numberOfDependentsOptions = text({ path: 'Dependants.Options' });
    // @ts-expect-error TS2339: Property 'variantTernary' does not exist on type 'null'.
    const { variantTernary } = useContext(FormUtilsContext);
    // @ts-expect-error TS2339: Property 'submitAttempted' does not exist on type ''.
    const { submitAttempted } = useLandmarkRefsContext();
    const [hasRentOrMortgageFocused, setHasRentOrMortgageFocused] = useState(false);

    const outgoingsIDs = FORM_SECTIONS.Outgoings;

    watch(outgoingsIDs.RentOrMortgageRate);

    return (
        <>
            <Box>
                <NarrativeTextHookForm data-qm-allow>
                    <Text path="Heading" />
                </NarrativeTextHookForm>

                <DependentsRadioGroup
                    id={outgoingsIDs.Dependants}
                    name={outgoingsIDs.Dependants}
                    variant="square"
                    optionWrapperProps={{
                        width: '6rem',
                        // @ts-expect-error TS2769: No overload matches this call.
                        margin: '0'
                    }}
                    aria-describedby={getAriaDescribedBy(
                        outgoingsIDs.Dependants,
                        submitAttempted,
                        [`${outgoingsIDs.Dependants}-helper-text`],
                        errors
                    )}
                    data-qa-id={`${outgoingsIDs.Dependants}-fieldset`}
                >
                    <LabelSection
                        paddingBottom={'xs'}
                        id={outgoingsIDs.Dependants}
                        label={<Text path="Dependants.Heading"></Text>}
                        as="legend"
                        py={'xs'}
                        // @ts-expect-error TS2554: Expected 5 arguments, but got 3.
                        displayTick={shouldDisplayRadioTick(outgoingsIDs.Dependants, watch, errors)}
                        data-qm-allow
                    />
                    <Box pb="xs">
                        <DescriptionSection
                            description={text({ path: 'Dependants.Description' })}
                            id={outgoingsIDs.Dependants}
                        />
                    </Box>
                    {Object.entries(numberOfDependentsOptions)
                        // @ts-expect-error TS2362: The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum t
                        .sort(([valueA], [valueB]) => valueA - valueB)
                        .map(([value, displayName]) => (
                            <Radio
                                key={`title-button-${displayName}`}
                                value={value}
                                {...register(outgoingsIDs.Dependants, {
                                    required: 'Number of dependants is required'
                                })}
                                data-qm-allow
                            >
                                {/* @ts-expect-error TS2322: Type 'unknown' is not assignable to type 'ReactNode'. */}
                                {displayName}
                            </Radio>
                        ))}
                </DependentsRadioGroup>
                <ErrorHandler
                    id={outgoingsIDs.Dependants}
                    show={touchedFields[outgoingsIDs.Dependants]}
                    data-qm-allow
                />

                <InputWrapper>
                    <RadioGroup
                        // @ts-expect-error TS2322: Property 'id' does not exist on the RadioGroup props type.
                        id={outgoingsIDs.CashAdvance}
                        name={outgoingsIDs.CashAdvance}
                        variant="square"
                        optionWrapperProps={{
                            width: '9.7rem',
                            my: 'xs'
                        }}
                        aria-describedby={getAriaDescribedBy(outgoingsIDs.CashAdvance, submitAttempted, [], errors)}
                        data-qa-id={`${outgoingsIDs.CashAdvance}-fieldset`}
                    >
                        <LabelSection
                            id={outgoingsIDs.CashAdvance}
                            label={<Text path="CashAdvance.Heading"></Text>}
                            as="legend"
                            pt="xs"
                            // @ts-expect-error TS2554: Expected 5 arguments, but got 3.
                            displayTick={shouldDisplayRadioTick(outgoingsIDs.CashAdvance, watch, errors)}
                            data-qm-allow
                        />
                        {Object.entries(cashAdvanceOptions).map(([displayName, value]) => (
                            <Radio
                                {...register(outgoingsIDs.CashAdvance)}
                                value={value}
                                key={`${outgoingsIDs.CashAdvance}-${value}`}
                                data-qm-allow
                            >
                                {displayName}
                            </Radio>
                        ))}
                    </RadioGroup>
                </InputWrapper>
                <InputWrapper>
                    <LivingSituationRadioGroup
                        id={outgoingsIDs.ResidentialStatus}
                        name={outgoingsIDs.ResidentialStatus}
                        variant="square"
                        optionWrapperProps={{
                            width: 'auto',
                            // @ts-expect-error TS2769: No overload matches this call.
                            margin: '0'
                        }}
                        aria-describedby={getAriaDescribedBy(
                            outgoingsIDs.ResidentialStatus,
                            submitAttempted,
                            [],
                            errors
                        )}
                        data-qa-id={`${outgoingsIDs.ResidentialStatus}-fieldset`}
                    >
                        <LabelSection
                            id={outgoingsIDs.ResidentialStatus}
                            label={<Text path="ResidentialStatus.Heading"></Text>}
                            // @ts-expect-error TS2554: Expected 5 arguments, but got 3.
                            displayTick={shouldDisplayRadioTick(outgoingsIDs.ResidentialStatus, watch, errors)}
                            my="0"
                            py="xs"
                            as="legend"
                            data-qm-allow
                        />
                        {Object.entries(residentialStatusOptions).map(([value, displayName]) => (
                            <Radio
                                key={`livingSituation-button-${value}`}
                                {...register(outgoingsIDs.ResidentialStatus, {
                                    required: 'Please select an option for Residential status'
                                })}
                                value={value}
                                data-qm-allow
                            >
                                {/* @ts-expect-error TS2322: Type 'unknown' is not assignable to type 'ReactNode'. */}
                                {displayName}
                            </Radio>
                        ))}
                    </LivingSituationRadioGroup>
                    <ErrorHandler
                        id={outgoingsIDs.ResidentialStatus}
                        show={touchedFields[outgoingsIDs.ResidentialStatus]}
                        data-qm-allow
                    />
                </InputWrapper>

                <Group>
                    {/* @ts-expect-error TS2769: No overload matches this call. */}
                    <InputWrapper my="0!important">
                        <LabelSection
                            id={outgoingsIDs.RentOrMortgageRate}
                            label={<Text path="RentMortgagePayments.Heading"></Text>}
                            displayTick={shouldDisplayFormTick(outgoingsIDs.RentOrMortgageRate, getValues, errors)}
                            pt="sm"
                            data-qm-allow
                        />
                        <DescriptionSection
                            description={text({ path: 'RentMortgagePayments.Description' })}
                            id={outgoingsIDs.RentOrMortgageRate}
                        />

                        <Controller
                            name={outgoingsIDs.RentOrMortgageRate}
                            rules={{
                                required: 'Please enter your personal monthly rent/mortgage contribution',
                                maxLength: { value: 7, message: 'Rent/mortgage payments must be a max of 7 digits' }
                            }}
                            render={({ field }: any) => {
                                const additionalIDs = [`${outgoingsIDs.RentOrMortgageRate}-helper-text`];
                                if (hasRentOrMortgageFocused) {
                                    additionalIDs.push(`${outgoingsIDs.RentOrMortgageRate}-explanation-box-text`);
                                }
                                return (
                                    <CurrencyInput
                                        id={outgoingsIDs.RentOrMortgageRate}
                                        name={field.name}
                                        value={field.value ?? ''}
                                        onValueChange={(value: any) => {
                                            field.onChange(value ?? '');
                                        }}
                                        onFocus={() => setHasRentOrMortgageFocused(true)}
                                        onBlur={field.onBlur}
                                        allowDecimals={false}
                                        allowNegativeValue={false}
                                        explanation={{
                                            Icon: CalendarIcon,
                                            body: (
                                                <span id={`${outgoingsIDs.RentOrMortgageRate}-explanation-box-text`}>
                                                    <JsxText
                                                        path="RentMortgagePayments.Explanation"
                                                        params={{
                                                            BOLD_TEXT: <b>go up</b>
                                                        }}
                                                    ></JsxText>
                                                </span>
                                            ),
                                            textId: 'explanation-text',
                                            prefix: outgoingsIDs.RentOrMortgageRate
                                        }}
                                        variant={variantTernary(outgoingsIDs.RentOrMortgageRate)}
                                        aria-describedby={getAriaDescribedBy(
                                            outgoingsIDs.RentOrMortgageRate,
                                            submitAttempted,
                                            additionalIDs,
                                            errors
                                        )}
                                        data-qa-id={outgoingsIDs.RentOrMortgageRate}
                                    />
                                );
                            }}
                        />
                        <ErrorHandler
                            id={outgoingsIDs.RentOrMortgageRate}
                            show={touchedFields[outgoingsIDs.RentOrMortgageRate]}
                            data-qm-allow
                        />
                    </InputWrapper>
                </Group>
            </Box>
        </>
    );
};
FinancialDependencyForm.propTypes = {
    Text: PropTypes.func.isRequired,
    JsxText: PropTypes.func.isRequired
};

export default withText('Outgoings')(FinancialDependencyForm);
